import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Search, TrendingUp, Globe, BarChart3, Plus, Trash2, Loader2, Sparkles,
  FileText, Target, RefreshCw, ExternalLink, Calendar, Clock, CheckCircle
} from "lucide-react";
import { ModuleDescription } from "@/components/ModuleDescription";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function KeywordResearch() {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scData, setScData] = useState(null);
  const [scLoading, setScLoading] = useState(false);
  const [autocompleteQuery, setAutocompleteQuery] = useState("");
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [acLoading, setAcLoading] = useState(false);
  const [trends, setTrends] = useState([]);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [newTopics, setNewTopics] = useState("");
  const [newCities, setNewCities] = useState("");
  const [volumeKeywords, setVolumeKeywords] = useState("");
  const [volumeResults, setVolumeResults] = useState([]);
  const [volumeLoading, setVolumeLoading] = useState(false);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiSources, setAiSources] = useState(null);
  // Keyword Planner
  const [kpQuery, setKpQuery] = useState("");
  const [kpResults, setKpResults] = useState(null);
  const [kpLoading, setKpLoading] = useState(false);
  // Topic Planner
  const [planner, setPlanner] = useState(null);
  const [plannerLoading, setPlannerLoading] = useState(false);
  const [matchResults, setMatchResults] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [siteSuggestions, setSiteSuggestions] = useState(null);
  const [siteSugLoading, setSiteSugLoading] = useState(false);
  const [selectedKpSite, setSelectedKpSite] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [sitesRes, gsRes, trendsRes] = await Promise.all([
          axios.get(`${API}/sites?status=active`),
          axios.get(`${API}/google/status`),
          axios.get(`${API}/keyword-research/trends`),
        ]);
        setSites(Array.isArray(sitesRes.data) ? sitesRes.data : sitesRes.data?.sites || []);
        setGoogleStatus(gsRes.data);
        if (trendsRes?.data?.topics) setTrends(trendsRes.data.topics);
      } catch(e) {}
      setLoading(false);
    };
    load();
  }, []);

  const loadSiteData = async (site) => {
    setSelectedSite(site);
    try { const res = await axios.get(`${API}/sites/${site.id}/content-templates`); setTemplates(res.data.templates || []); } catch(e) { setTemplates([]); }
    setScData(null);
  };
  const loadSearchConsole = async () => {
    if (!selectedSite) return; setScLoading(true);
    try { const res = await axios.get(`${API}/keyword-research/search-console/${selectedSite.id}`); setScData(res.data); } catch(e) { toast.error("Blad SC"); }
    setScLoading(false);
  };
  const searchAutocomplete = async () => {
    if (!autocompleteQuery.trim()) return; setAcLoading(true);
    try { const res = await axios.get(`${API}/keyword-research/autocomplete/${encodeURIComponent(autocompleteQuery)}`); setAutocompleteResults(res.data.suggestions || []); } catch(e) {}
    setAcLoading(false);
  };
  const loadTrends = async () => {
    setTrendsLoading(true);
    try { const res = await axios.get(`${API}/keyword-research/trends`); setTrends(res.data.topics || []); } catch(e) {}
    setTrendsLoading(false);
  };
  const saveTemplates = async () => {
    if (!selectedSite) return; setSaving(true);
    try { await axios.put(`${API}/sites/${selectedSite.id}/content-templates`, {templates}); toast.success("Zapisano!"); } catch(e) { toast.error("Blad"); }
    setSaving(false);
  };
  const addTemplate = () => {
    if (!newTopics.trim()) return;
    setTemplates(t => [...t, { topics: newTopics.split("|").map(s=>s.trim()).filter(Boolean), cities: newCities ? newCities.split("|").map(s=>s.trim()).filter(Boolean) : [] }]);
    setNewTopics(""); setNewCities("");
  };
  const aiSuggestTemplates = async () => {
    if (!selectedSite) return; setAiSuggesting(true); setAiSources(null);
    try {
      const res = await axios.post(`${API}/sites/${selectedSite.id}/suggest-content-templates`);
      if (res.data.templates?.length > 0) { setTemplates(t => [...t, ...res.data.templates]); setAiSources(res.data.sources); toast.success(`AI: ${res.data.templates.length} szablonow!`); }
      else toast.error(res.data.error || "Brak sugestii");
    } catch(e) { toast.error("Blad AI"); }
    setAiSuggesting(false);
  };
  const connectGoogle = async () => {
    try { const res = await axios.get(`${API}/google/auth-url`); window.open(res.data.url, "_blank", "width=600,height=700"); } catch(e) { toast.error("Blad"); }
  };
  // Keyword Planner
  const runKeywordPlanner = async () => {
    if (!kpQuery.trim()) return; setKpLoading(true);
    try {
      const res = await axios.post(`${API}/keyword-planner/research`, { keyword: kpQuery, site_id: selectedSite?.id });
      setKpResults(res.data);
    } catch(e) { toast.error("Blad"); }
    setKpLoading(false);
  };
  // Topic Planner
  const matchKeywordsToSites = async () => {
    if (!kpResults?.keywords?.length) return;
    setMatchLoading(true);
    try {
      const kws = kpResults.keywords.slice(0, 15).map(k => k.keyword);
      const res = await axios.post(`${API}/keyword-planner/match-sites`, {keywords: kws});
      setMatchResults(res.data);
    } catch(e) { toast.error("Blad"); }
    setMatchLoading(false);
  };
  const suggestForSite = async () => {
    if (!selectedKpSite) return;
    setSiteSugLoading(true);
    try {
      const res = await axios.post(`${API}/keyword-planner/suggest-for-site`, {site_id: selectedKpSite});
      setSiteSuggestions(res.data);
    } catch(e) { toast.error("Blad"); }
    setSiteSugLoading(false);
  };
  const addToSiteTemplates = async (siteId, siteName, topics) => {
    try {
      await axios.post(`${API}/keyword-planner/add-to-templates`, {site_id: siteId, topics});
      toast.success(`Dodano ${topics.length} tematow do ${siteName}!`);
    } catch(e) { toast.error("Blad"); }
  };

  const loadPlanner = async () => {
    setPlannerLoading(true);
    try { const res = await axios.get(`${API}/topic-planner`); setPlanner(res.data); } catch(e) { toast.error("Blad"); }
    setPlannerLoading(false);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="keyword-research-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Keyword Research & Planer</h1>
          <p className="text-muted-foreground text-sm mt-1">Szablony, Keyword Planner, Search Console, Planer tematow</p>
        </div>
      <ModuleDescription title="Jak dziala Keyword Research?">
        <p><strong>Keyword Research</strong> to narzedzie do planowania tresci SEO z wieloma zrodlami danych.</p>
        <p><strong>Tab Szablony & SC:</strong> Konfiguracja szablonow tematow dla kazdej strony + dane z Google Search Console (frazy, pozycje, kliki).</p>
        <p><strong>Tab Planer tematow:</strong> Inteligentne sugestie tematow na podstawie Google Autocomplete, Trends i Search Console.</p>
        <p><strong>Tab Keyword Planner:</strong> Wyszukiwanie fraz kluczowych z autocomplete i analiza popularnosci.</p>
        <p><strong>Tab Wolumeny:</strong> Smart Volume Estimator — szacuje wolumen wyszukiwan na podstawie Google Trends, glebokosci Autocomplete i danych GSC. Wpisz frazy oddzielone przecinkami.</p>
      </ModuleDescription>

        <div className="flex items-center gap-2">
          {googleStatus?.connected && <Badge className="bg-green-100 text-green-800"><Globe className="w-3 h-3 mr-1" /> Google SC</Badge>}
          <Button size="sm" variant="outline" onClick={async () => {
            try { await axios.post(`${API}/keyword-planner/auto-update-all`); toast.success("AI aktualizuje szablony dla WSZYSTKICH stron w tle!"); } catch(e) { toast.error("Blad"); }
          }} className="gap-1.5 text-purple-600 border-purple-200">
            <Sparkles className="w-3.5 h-3.5" /> Auto AI szablony
          </Button>
          <Button size="sm" variant="outline" onClick={connectGoogle} className="gap-1.5">
            <Globe className="w-3.5 h-3.5" /> {googleStatus?.connected ? "Polacz ponownie" : "Polacz Google"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="templates" className="w-full">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="templates"><Target className="w-3.5 h-3.5 mr-1.5" />Szablony & SC</TabsTrigger>
          <TabsTrigger value="planner" onClick={() => !planner && loadPlanner()}><Calendar className="w-3.5 h-3.5 mr-1.5" />Planer tematow</TabsTrigger>
          <TabsTrigger value="keywords"><Search className="w-3.5 h-3.5 mr-1.5" />Keyword Planner</TabsTrigger>
          <TabsTrigger value="volumes"><BarChart3 className="w-3.5 h-3.5 mr-1.5" />Wolumeny</TabsTrigger>
        </TabsList>

        {/* TAB 1: Szablony + Search Console */}
        <TabsContent value="templates">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Wybierz strone</CardTitle></CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[550px]">
                  <div className="space-y-0.5 p-3">
                    {sites.map(site => (
                      <button key={site.id} onClick={() => loadSiteData(site)}
                        className={`w-full text-left px-2 py-1.5 rounded text-xs hover:bg-muted/50 ${selectedSite?.id === site.id ? "bg-primary/10 font-medium" : ""}`}>
                        {site.name}
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
            <div className="lg:col-span-3 space-y-4">
              {!selectedSite ? <Card><CardContent className="py-12 text-center text-muted-foreground">Wybierz strone</CardContent></Card> : (<>
                <Card><CardContent className="p-4 flex items-center justify-between">
                  <div><h2 className="text-lg font-bold">{selectedSite.name}</h2><p className="text-xs text-muted-foreground">{selectedSite.category} | {selectedSite.url}</p></div>
                  <Button size="sm" variant="outline" onClick={loadSearchConsole} disabled={scLoading || !googleStatus?.connected} className="gap-1.5">
                    {scLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BarChart3 className="w-3.5 h-3.5" />} Search Console
                  </Button>
                </CardContent></Card>
                {/* Szablony */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2"><Target className="w-4 h-4" /> Szablony tematow</CardTitle>
                    <p className="text-xs text-muted-foreground">Tematy dla Auto Publishera. Uzyj {"{miasto}"} dla lokalnych.</p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {templates.map((tpl, idx) => (
                      <div key={idx} className="flex items-start gap-2 border rounded-lg p-2.5">
                        <div className="flex-1 text-xs">
                          <p className="font-medium">{tpl.topics?.join(" | ")}</p>
                          {tpl.cities?.length > 0 && <p className="text-muted-foreground mt-0.5">Miasta: {tpl.cities.join(", ")}</p>}
                          {tpl.description && <p className="text-muted-foreground mt-0.5 italic">{tpl.description}</p>}
                        </div>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => setTemplates(t => t.filter((_, i) => i !== idx))}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    ))}
                    <div className="border-2 border-dashed rounded-lg p-3 space-y-2">
                      <Input placeholder="Tematy (oddziel |)" value={newTopics} onChange={e => setNewTopics(e.target.value)} className="text-xs h-8" />
                      <Input placeholder="Miasta (oddziel |) opcjonalne" value={newCities} onChange={e => setNewCities(e.target.value)} className="text-xs h-8" />
                      <Button size="sm" variant="outline" className="w-full gap-1 text-xs" onClick={addTemplate}><Plus className="w-3 h-3" /> Dodaj</Button>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={aiSuggestTemplates} disabled={aiSuggesting} variant="outline" className="flex-1 gap-1.5 text-purple-600 border-purple-200">
                        {aiSuggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                        {aiSuggesting ? "AI analizuje..." : "AI Sugeruj"}
                      </Button>
                      <Button onClick={saveTemplates} disabled={saving} className="flex-1 gap-1.5">
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} Zapisz
                      </Button>
                    </div>
                    {aiSources && <div className="flex gap-1 flex-wrap text-[10px]">
                      {aiSources.autocomplete > 0 && <Badge variant="secondary" className="text-[9px]">AC: {aiSources.autocomplete}</Badge>}
                      {aiSources.trends > 0 && <Badge variant="secondary" className="text-[9px]">Trends: {aiSources.trends}</Badge>}
                      {aiSources.search_console > 0 && <Badge variant="secondary" className="text-[9px]">SC: {aiSources.search_console}</Badge>}
                    </div>}
                  </CardContent>
                </Card>
                {/* SC Data */}
                {scData && !scData.error && (<Card><CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Search Console <Badge variant="outline" className="text-[10px]">{scData.period}</Badge></CardTitle></CardHeader>
                  <CardContent>
                    {scData.growth_potential?.length > 0 && (<div className="mb-4"><p className="text-xs font-semibold text-amber-700 mb-2">Frazy z potencjalem (poz. 5-20):</p>
                      {scData.growth_potential.map((kw, i) => (<div key={i} className="flex items-center justify-between text-xs border rounded p-2 mb-1"><span className="font-medium flex-1">{kw.query}</span><div className="flex gap-3 text-muted-foreground"><span>Poz: <strong className="text-amber-600">{kw.position}</strong></span><span>{kw.clicks} klik</span><span>{kw.impressions} wyswietl</span></div></div>))}
                    </div>)}
                    {scData.top_keywords?.length > 0 && (<div><p className="text-xs font-semibold mb-2">Top frazy ({scData.total_keywords}):</p>
                      {scData.top_keywords.slice(0,15).map((kw, i) => (<div key={i} className="flex items-center justify-between text-xs py-1 border-b"><span>{kw.query}</span><span className={kw.position <= 3 ? "text-green-600 font-bold" : kw.position <= 10 ? "text-amber-600" : "text-red-500"}>#{kw.position} | {kw.clicks} klik</span></div>))}
                    </div>)}
                  </CardContent></Card>)}
                {scData?.error && <Card><CardContent className="p-4 text-xs text-red-500">{scData.error}</CardContent></Card>}
                {/* Autocomplete + Trends */}
                <div className="grid grid-cols-2 gap-4">
                  <Card><CardHeader className="pb-2"><CardTitle className="text-sm"><Search className="w-3.5 h-3.5 inline mr-1" />Autocomplete</CardTitle></CardHeader>
                    <CardContent><div className="flex gap-2 mb-2"><Input placeholder="Fraza..." value={autocompleteQuery} onChange={e => setAutocompleteQuery(e.target.value)} onKeyDown={e => e.key==="Enter" && searchAutocomplete()} className="text-xs h-8" /><Button size="sm" onClick={searchAutocomplete} disabled={acLoading} className="h-8">{acLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}</Button></div>
                      {autocompleteResults.map((s, i) => <div key={i} className="text-xs py-1 px-2 rounded hover:bg-muted/50"><Search className="w-3 h-3 inline mr-1 text-muted-foreground" />{s}</div>)}
                    </CardContent></Card>
                  <Card><CardHeader className="pb-2"><div className="flex justify-between"><CardTitle className="text-sm"><TrendingUp className="w-3.5 h-3.5 inline mr-1" />Trends PL</CardTitle><Button size="sm" variant="ghost" onClick={loadTrends} className="h-6">{trendsLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}</Button></div></CardHeader>
                    <CardContent>{trends.length === 0 ? <p className="text-xs text-center text-muted-foreground py-4">Kliknij odswierz</p> : trends.map((t, i) => <div key={i} className="text-xs py-1 flex justify-between"><span><TrendingUp className="w-3 h-3 inline mr-1 text-green-500" />{t.title}</span>{t.traffic && <Badge variant="secondary" className="text-[9px]">{t.traffic}</Badge>}</div>)}</CardContent></Card>
                </div>
              </>)}
            </div>
          </div>
        </TabsContent>

        {/* TAB 2: Planer tematow */}
        <TabsContent value="planner">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold">Planer tematow Auto Publishera</h2>
              <Button size="sm" onClick={loadPlanner} disabled={plannerLoading} className="gap-1.5">
                {plannerLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Odswierz
              </Button>
            </div>
            {!planner ? <Card><CardContent className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /><p className="text-sm text-muted-foreground">Ladowanie planera...</p></CardContent></Card> : (<>
              {/* Statystyki */}
              <div className="grid grid-cols-4 gap-3">
                <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-primary">{planner.stats.main_articles_week}</p><p className="text-xs text-muted-foreground">Glowne (7 dni)</p></CardContent></Card>
                <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-blue-600">{planner.stats.support_articles_week}</p><p className="text-xs text-muted-foreground">Wspierajace (7 dni)</p></CardContent></Card>
                <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-green-600">{planner.stats.unique_sites_week}</p><p className="text-xs text-muted-foreground">Stron obsluzone</p></CardContent></Card>
                <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-amber-600">{planner.stats.total_enabled}</p><p className="text-xs text-muted-foreground">Wlaczonych stron</p></CardContent></Card>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Nadchodzace */}
                <Card><CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4" /> Nadchodzace publikacje</CardTitle></CardHeader>
                  <CardContent><ScrollArea className="h-[400px]"><div className="space-y-1">
                    {planner.upcoming.map((u, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1.5 px-2 rounded hover:bg-muted/50 border-b">
                        <span className="font-medium">{u.site_name}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant={u.hours_left === 0 ? "default" : "outline"} className="text-[9px]">
                            {u.hours_left === 0 ? "TERAZ" : `za ${u.hours_left}h`}
                          </Badge>
                          <span className="text-muted-foreground">co {u.interval_days}d</span>
                        </div>
                      </div>
                    ))}
                  </div></ScrollArea></CardContent></Card>
                {/* Ostatnie */}
                <Card><CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Ostatnie publikacje (7 dni)</CardTitle></CardHeader>
                  <CardContent><ScrollArea className="h-[400px]"><div className="space-y-1.5">
                    {planner.recent_main.map((p, i) => (
                      <div key={i} className="border rounded-lg p-2 text-xs">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{p.title}</p>
                            <p className="text-muted-foreground">{p.site_name} | {new Date(p.created_at).toLocaleDateString("pl-PL")}</p>
                          </div>
                          {p.published_url && <a href={p.published_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-3 h-3 text-blue-500" /></a>}
                        </div>
                      </div>
                    ))}
                  </div></ScrollArea></CardContent></Card>
              </div>
            </>)}
          </div>
        </TabsContent>

        {/* TAB 3: Keyword Planner */}
        <TabsContent value="keywords">
          <div className="space-y-4">
            {/* Szukaj fraz lub wybierz strone */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm"><Search className="w-3.5 h-3.5 inline mr-1" />Szukaj fraz kluczowych</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Input placeholder="Wpisz fraze np. pozyczki online, kebab koszalin..." value={kpQuery} onChange={e => setKpQuery(e.target.value)} onKeyDown={e => e.key==="Enter" && runKeywordPlanner()} className="text-sm" />
                    <Button onClick={runKeywordPlanner} disabled={kpLoading} className="gap-1.5">
                      {kpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Szukaj
                    </Button>
                  </div>
                </CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm"><Sparkles className="w-3.5 h-3.5 inline mr-1" />AI sugeruj frazy dla strony</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <select value={selectedKpSite} onChange={e => setSelectedKpSite(e.target.value)} className="flex-1 text-sm border rounded px-2 h-10">
                      <option value="">Wybierz strone...</option>
                      {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <Button onClick={suggestForSite} disabled={siteSugLoading || !selectedKpSite} className="gap-1.5 bg-purple-600 hover:bg-purple-700">
                      {siteSugLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} AI Sugeruj
                    </Button>
                  </div>
                </CardContent></Card>
            </div>
            {/* AI sugestie dla strony */}
            {siteSuggestions && (
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">AI sugestie fraz dla: {siteSuggestions.site_name}</CardTitle></CardHeader>
                <CardContent><div className="space-y-1.5">
                  {siteSuggestions.suggestions?.map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1.5 px-2 rounded border hover:bg-muted/50">
                      <div className="flex-1">
                        <span className="font-medium">{s.keyword}</span>
                        <span className="text-muted-foreground ml-2">{s.reason}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={s.priority === "high" ? "default" : "secondary"} className="text-[9px]">{s.priority}</Badge>
                        <Badge variant="outline" className="text-[9px]">{s.type}</Badge>
                        <Button size="sm" variant="ghost" className="h-6 text-[10px] text-blue-600" onClick={() => {setKpQuery(s.keyword); runKeywordPlanner();}}>Szukaj</Button>
                      </div>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" className="w-full text-xs mt-2 gap-1" onClick={() => {
                    const topics = siteSuggestions.suggestions.filter(s => s.priority === "high").map(s => s.keyword);
                    if (topics.length > 0) addToSiteTemplates(selectedKpSite, siteSuggestions.site_name, topics);
                  }}><Plus className="w-3 h-3" /> Dodaj high-priority do szablonow strony</Button>
                </div></CardContent></Card>
            )}
            {kpResults && (<>
              <div className="grid grid-cols-4 gap-3">
                <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-primary">{kpResults.total}</p><p className="text-xs text-muted-foreground">Znalezionych fraz</p></CardContent></Card>
                <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-blue-600">{kpResults.trends?.length || 0}</p><p className="text-xs text-muted-foreground">Z danymi Trends</p></CardContent></Card>
                <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-green-600">{kpResults.search_console_related?.length || 0}</p><p className="text-xs text-muted-foreground">Z Search Console</p></CardContent></Card>
                <Card className="cursor-pointer hover:border-purple-300" onClick={matchKeywordsToSites}><CardContent className="p-3 text-center">
                  {matchLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : <Sparkles className="w-5 h-5 text-purple-600 mx-auto" />}
                  <p className="text-xs text-purple-600 font-medium mt-1">AI Dopasuj do stron</p>
                </CardContent></Card>
              </div>
              {matchResults?.matches?.length > 0 && (
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm"><Target className="w-3.5 h-3.5 inline mr-1" />AI dopasowanie fraz do stron</CardTitle></CardHeader>
                  <CardContent><div className="space-y-2">
                    {matchResults.matches.map((m, i) => (
                      <div key={i} className="border rounded-lg p-2.5 text-xs">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-bold">{m.keyword}</p>
                            <p className="text-muted-foreground mt-0.5">{m.reason}</p>
                          </div>
                          <div className="flex gap-1">
                            {m.sites?.map((s, j) => (
                              <Button key={j} size="sm" variant="outline" className="h-6 text-[10px] gap-1" onClick={() => addToSiteTemplates(m.site_ids?.[j] || "", s, [m.template_topic || m.keyword])}>
                                <Plus className="w-2.5 h-2.5" />{s}
                              </Button>
                            ))}
                          </div>
                        </div>
                        {m.template_topic && <p className="text-blue-600 mt-1">Temat: {m.template_topic}</p>}
                      </div>
                    ))}
                  </div></CardContent></Card>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Frazy kluczowe ({kpResults.keywords?.length})</CardTitle></CardHeader>
                  <CardContent><ScrollArea className="h-[400px]"><div className="space-y-1">
                    {kpResults.keywords?.map((kw, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1.5 px-2 rounded hover:bg-muted/50">
                        <span>{kw.keyword}</span>
                        <div className="flex gap-2">
                          <Badge variant="secondary" className="text-[9px]">{kw.source}</Badge>
                          {kw.popularity !== null && <Badge className="text-[9px] bg-green-100 text-green-800">pop: {kw.popularity}</Badge>}
                        </div>
                      </div>
                    ))}
                  </div></ScrollArea></CardContent></Card>
                <div className="space-y-4">
                  {kpResults.trends?.length > 0 && (<Card><CardHeader className="pb-2"><CardTitle className="text-sm"><TrendingUp className="w-3.5 h-3.5 inline mr-1" />Popularnosc (Trends)</CardTitle></CardHeader>
                    <CardContent>{kpResults.trends.map((t, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b">
                        <span>{t.keyword}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2"><div className="bg-green-500 h-2 rounded-full" style={{width: `${t.avg_interest}%`}}></div></div>
                          <span className="text-muted-foreground w-8 text-right">{t.avg_interest}</span>
                        </div>
                      </div>
                    ))}</CardContent></Card>)}
                  {kpResults.search_console_related?.length > 0 && (<Card><CardHeader className="pb-2"><CardTitle className="text-sm"><BarChart3 className="w-3.5 h-3.5 inline mr-1" />Search Console</CardTitle></CardHeader>
                    <CardContent>{kpResults.search_console_related.map((kw, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b">
                        <span>{kw.query}</span>
                        <span className="text-muted-foreground">poz: {kw.position} | {kw.clicks} klik</span>
                      </div>
                    ))}</CardContent></Card>)}
                </div>
              </div>
            </>)}
          </div>
        </TabsContent>

        {/* TAB 4: Volume Estimator */}
        <TabsContent value="volumes">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-1.5">
                <BarChart3 className="w-4 h-4 text-violet-500" /> Smart Volume Estimator
                <Badge variant="outline" className="text-[9px] ml-1">Google Trends + Autocomplete + GSC</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Input
                  placeholder="Wpisz frazy (oddzielone przecinkami)... np: bitcoin, kryptowaluty, ethereum"
                  value={volumeKeywords}
                  onChange={e => setVolumeKeywords(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" && volumeKeywords.trim()) {
                      setVolumeLoading(true);
                      const kws = volumeKeywords.split(",").map(k => k.trim()).filter(Boolean);
                      axios.post(`${API}/volume/batch`, { keywords: kws, site_id: selectedSite?.id }).then(res => {
                        setVolumeResults(res.data.results || []);
                      }).catch(() => {}).finally(() => setVolumeLoading(false));
                    }
                  }}
                  data-testid="volume-keywords-input"
                />
                <Button
                  disabled={volumeLoading || !volumeKeywords.trim()}
                  onClick={() => {
                    setVolumeLoading(true);
                    const kws = volumeKeywords.split(",").map(k => k.trim()).filter(Boolean);
                    axios.post(`${API}/volume/batch`, { keywords: kws, site_id: selectedSite?.id }).then(res => {
                      setVolumeResults(res.data.results || []);
                    }).catch(() => {}).finally(() => setVolumeLoading(false));
                  }}
                  className="gap-1.5 bg-violet-600 hover:bg-violet-700 shrink-0"
                  data-testid="volume-estimate-btn"
                >
                  {volumeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
                  Oszacuj
                </Button>
              </div>

              {volumeResults.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="py-2 px-3 text-left font-medium">Fraza</th>
                        <th className="py-2 px-3 text-right font-medium">Vol. /mies.</th>
                        <th className="py-2 px-3 text-center font-medium">Poziom</th>
                        <th className="py-2 px-3 text-center font-medium">Konkurencja</th>
                        <th className="py-2 px-3 text-right font-medium">Trends</th>
                        <th className="py-2 px-3 text-right font-medium">Autocomplete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {volumeResults.map((r, i) => (
                        <tr key={i} className="border-t hover:bg-muted/20">
                          <td className="py-2 px-3 font-medium">{r.keyword}</td>
                          <td className="py-2 px-3 text-right font-bold text-violet-600">
                            {r.estimated_monthly_volume?.toLocaleString()}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <Badge className={`text-[8px] ${
                              r.volume_label === "bardzo wysoki" ? "bg-violet-100 text-violet-700" :
                              r.volume_label === "wysoki" ? "bg-blue-100 text-blue-700" :
                              r.volume_label === "sredni" ? "bg-green-100 text-green-700" :
                              r.volume_label === "niski" ? "bg-amber-100 text-amber-700" :
                              "bg-gray-100 text-gray-600"
                            }`}>{r.volume_label}</Badge>
                          </td>
                          <td className="py-2 px-3 text-center">
                            <Badge className={`text-[8px] ${
                              r.competition?.difficulty === "wysoka" ? "bg-red-100 text-red-700" :
                              r.competition?.difficulty === "srednia" ? "bg-amber-100 text-amber-700" :
                              "bg-green-100 text-green-700"
                            }`}>{r.competition?.difficulty || "?"}</Badge>
                          </td>
                          <td className="py-2 px-3 text-right text-muted-foreground">{r.trends_score || "-"}</td>
                          <td className="py-2 px-3 text-right text-muted-foreground">
                            {r.autocomplete?.position >= 0 ? `#${r.autocomplete.position + 1}` : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {volumeResults.length === 0 && !volumeLoading && (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  Wpisz frazy kluczowe oddzielone przecinkami i kliknij Oszacuj
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
}
